import {
  COMPARISON_HIDEABLE_FIELDS,
  type ComparisonSummaryCard,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { COMPARISON_TABLE_SORT_CAPTIONS } from './production-comparison-table.state';

export type ChartDatasetKey = 'paid' | 'submit';
export type ComparisonPresetMode = 'ytd' | 'quarter' | 'ranges';
export type QuarterValue = 'Q1' | 'Q2' | 'Q3' | 'Q4';

/** A dataset's opening figures; built by the catalog, so the page, workbook and PDF agree. */
export type ComparisonKpiCard = ComparisonSummaryCard;

export type ComparisonSetupState = {
  mode: ComparisonPresetMode;
  currentYear: number;
  compareYear: number;
  stopDate: string;
  currentRangeStart: string;
  currentRangeEnd: string;
  comparisonRangeStart: string;
  comparisonRangeEnd: string;
  quarters: QuarterValue[];
  mgaTopCount: number;
  agentTopCount: number;
};

export type SelectOption<T extends string | number> = {
  id: T;
  text: string;
};

export const COMPARISON_MODE_OPTIONS: SelectOption<ComparisonPresetMode>[] = [
  { id: 'ytd', text: 'YTD' },
  // { id: 'quarter', text: 'Quarter' },
  { id: 'ranges', text: 'Ranges' },
];

export const QUARTER_OPTIONS: SelectOption<QuarterValue>[] = [
  { id: 'Q1', text: 'Q1' },
  { id: 'Q2', text: 'Q2' },
  { id: 'Q3', text: 'Q3' },
  { id: 'Q4', text: 'Q4' },
];

export const CHART_DATASET_OPTIONS: SelectOption<ChartDatasetKey>[] = [
  { id: 'paid', text: 'Paid charts' },
  { id: 'submit', text: 'Submitted charts' },
];

export enum BlockDatasetOptions {
  paid = 'paid',
  submit = 'submit',
}

/**
 * One width per column, shared by every grid on the page.
 *
 * Without it each grid sizes its own columns to its own content, so the same column sits at a
 * different place in each table and appears to jump sideways as the page is scrolled. The
 * numbers are generous enough for the widest realistic value — a goal in the tens of millions,
 * a percentage past four digits — because a column that wraps is worse than one with slack.
 */
export const COMPARISON_COLUMN_WIDTHS: Readonly<Record<string, number | undefined>> = {
  // The name column is deliberately absent: it takes whatever width the fixed columns leave,
  // which is where slack belongs. Names vary in length and are the one thing worth reading in
  // full; a figure needs exactly the room its digits take.
  //
  // Every grid leaves the same slack, so the name column stretches by the same amount in each
  // and the columns after it still line up.
  // Deliberately the goal pair's combined width: an agent table carries MGA where an MGA table
  // carries Goal and % Goal Met, so matching the two keeps every column after them in line.
  mgaName: 260,
  goalAmount: 140,
  goalAttainedPercent: 120,
  currentPremium: 150,
  comparisonPremium: 150,
  premiumDifference: 150,
  premiumChangePercent: 140,
  currentPolicies: 130,
  comparisonPolicies: 130,
  policyDifference: 120,
  policyChangePercent: 130,
};

/** The name column never shrinks past this, however little room the fixed columns leave. */
export const COMPARISON_NAME_COLUMN_MIN_WIDTH = 220;

export type ComparisonHideableColumn = {
  field: string;
  caption: string;
};

/**
 * MGA is the one column the sort map does not name, being the only non-numeric column a grid
 * can do without. Everything else is already captioned there and is not repeated here.
 */
const COMPARISON_COLUMN_CAPTIONS: Readonly<Record<string, string>> = {
  mgaName: 'MGA',
  ...COMPARISON_TABLE_SORT_CAPTIONS,
};

/**
 * What the column pickers offer, in the order every grid declares its columns.
 *
 * Both membership and order come from the table catalog every grid is declared from, so a
 * column added there appears in the pickers without a second list to remember. The name
 * column is never among them: it identifies the row, and a table without it is a list of
 * unattributed numbers.
 *
 * No grid carries all of these. MGA is agent-level only and the goal pair belongs to the
 * datasets that have goals, so hiding a column reaches the grids that have it and is ignored
 * by the rest.
 */
export const COMPARISON_HIDEABLE_COLUMNS: readonly ComparisonHideableColumn[] = COMPARISON_HIDEABLE_FIELDS.map(
  field => ({ field, caption: COMPARISON_COLUMN_CAPTIONS[field] ?? field }),
);

/**
 * Identifies this dashboard's settings document.
 *
 * Not a ReportIds value: those name the reporting-API reports, and this is a dashboard.
 */
export const COMPARISON_DASHBOARD_REPORT_ID = 'comparison-dashboard';

/**
 * The settings document for one view of this report.
 *
 * A viewer sees the page at Alliance Group, RMD or MGA level, and the grids differ between
 * them — an MGA view carries no MGA column, an RMD view is narrowed to their own agencies. One
 * document for all three meant arranging the page at one level rearranged it at the others, so
 * the level is part of the document's name.
 */
export const buildComparisonSettingsId = (scope: string): string => `${COMPARISON_DASHBOARD_REPORT_ID}.${scope}`;
